/**
 * Case Management's loading shape.
 *
 * Read this file next to `./case-list-shell.tsx` — it is that component's
 * layout with the two regions that *need server data* swapped for
 * `<Skeleton />` (the "last updated" timestamp and the table body) and nothing
 * else changed. Everything
 * the page can draw without a response — the toolbar container, the search
 * input, the active-filter chips, the "Last updated:" label, the refresh
 * button — renders here for real, so it doesn't pop into existence when the
 * query resolves (P-05, P-17).
 *
 * Two boundaries worth knowing, because they're where the "render the real
 * chrome" rule stops:
 *
 * 1. **`CaseFilterBar`'s menu is shape-matched, not imported.** It is
 *    static chrome, but it drags `Calendar` and the Source option resolvers
 *    behind it, and this file is
 *    imported *eagerly* by the route while the real table is `lazy()` — reusing
 *    it here would undo the split (P-21). Reuse real chrome when it's cheap;
 *    reproduce its shape when reuse would move weight into the eager bundle.
 *    The look-alike and the chips do read `useCaseFilters` — URL state plus a
 *    non-suspense catalog read, which is light. What they cannot know yet is
 *    the Investigation flag rule, so they count and show `investigation` as if
 *    it were on; on a tenant with it off, a stale param shrinks away on load.
 * 2. **The column header row is a bar, not the real labels.** The headers are
 *    static, but they live inside the lazy chunk's column defs together with
 *    their flex widths; copying the strings here would drift, and copying them
 *    *without* the widths would render a header that lines up with nothing.
 */
import { ChevronDown, Filter, RefreshCcw } from 'lucide-react'

import { TableToolbar } from '@/shared/data-table/components/table-toolbar'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { useCaseFilters } from '../hooks/use-case-filters'
import { countUserFilters } from '../lib/case-list-params'
import { CaseFilterChips } from './filters/case-filter-chips'
import { CaseSearch } from './filters/case-search'
import { FilterCount } from './filters/filter-count'

import type { CaseListAnalyticsPrefix } from '../lib/case-list-analytics'

/**
 * Mirrors `TableLastUpdated` — the only part of the toolbar that needs data.
 *
 * The container classes are the real component's base merged with the override
 * `case-list-shell.tsx` passes it; keep the two in step or the toolbar's right
 * edge moves when the query lands. Refresh renders real-but-disabled: it exists, it's just
 * nothing to refresh yet.
 */
const CaseLastUpdatedSkeleton = () => (
	<div className="flex w-full shrink-0 grow-0 items-center gap-4 self-start pt-2 sm:w-fit sm:justify-end sm:self-auto">
		<p className="text-muted-foreground">
			{/* Sized to "less than a minute ago" — the string that renders the
			    instant this skeleton is replaced. Too narrow and the whole toolbar
			    re-flows: last-updated is the flex row's fixed end, so every pixel
			    it lacks is a pixel the search input steals. */}
			Last updated:{' '}
			{/* `Skeleton` renders a `div`, which is invalid inside a `p` — the
			    placeholder is a plain span styled to match it. */}
			<span className="bg-muted inline-block h-4 w-44 animate-pulse rounded-md align-middle" />
		</p>

		<Button
			variant="outline"
			size="icon"
			disabled
			title="Refresh"
			className="h-8 w-8"
		>
			<RefreshCcw className="h-4 w-4" />
		</Button>
	</div>
)

type CaseToolbarSkeletonProps = {
	analyticsPrefix: CaseListAnalyticsPrefix
	searchPlaceholder: string
}

/**
 * The toolbar row of both Case lists: Filters, search, last-updated. The
 * classes repeat `CaseFilterBar`'s — keep the two in step.
 *
 * `CaseSearch` is the real component, not a placeholder — it reads and writes
 * only the URL, so it keeps working while the table loads and whatever the user
 * types is already applied by the time rows arrive. The menu look-alike carries
 * its real count for the same reason: that is URL state too, and a count that
 * appears on load would widen the button.
 */
export const CaseToolbarSkeleton = ({
	analyticsPrefix,
	searchPlaceholder,
}: CaseToolbarSkeletonProps) => {
	const { state } = useCaseFilters()
	// Investigation counts as shown — the flag rule is not known yet (file header).
	const count = countUserFilters(state, { showInvestigation: true })

	return (
		<TableToolbar className="w-full">
			<div className="flex w-full flex-col gap-2 md:flex-row md:items-start">
				{/* Look-alike for the Filters menu — see the file header. */}
				<Button
					variant="outline"
					disabled
					className="flex h-12 shrink-0 flex-row items-center gap-2 self-start"
				>
					<Filter className="size-4" /> Filters
					<FilterCount count={count} label="active" />
					<ChevronDown className="size-4" />
				</Button>

				<CaseSearch
					analyticsEvent={`${analyticsPrefix}_search`}
					placeholder={searchPlaceholder}
				/>
			</div>

			<CaseLastUpdatedSkeleton />
		</TableToolbar>
	)
}

/**
 * Mirrors the row under the toolbar: the filter chips, then "N cases" at the
 * right. The chips are the real component — pure URL state, so chips the user
 * filtered by stay put instead of appearing a beat later and pushing the table
 * down. Only the count needs the response.
 */
export const CaseSummarySkeleton = ({
	analyticsPrefix,
}: {
	analyticsPrefix: CaseListAnalyticsPrefix
}) => (
	<div className="flex w-full flex-row items-start gap-4">
		{/* Investigation shown — the flag rule is not known yet (file header). */}
		<CaseFilterChips
			analyticsPrefix={analyticsPrefix}
			showInvestigation
			className="flex-1"
		/>
		<Skeleton className="ml-auto h-5 w-16 shrink-0" />
	</div>
)

type CaseTableSkeletonProps = {
	/** Enough rows to overflow the viewport; the container clips the excess. */
	rows?: number
}

/**
 * Mirrors the `DataTable` region.
 *
 * `flex-1 min-h-0` rather than a fixed block: the real table grows into the
 * remaining page height, so a short skeleton would let everything below it jump
 * upward. Row height tracks `ROW_HEIGHT` (48px) in
 * `shared/data-table/components/data-table.tsx`; the header bar tracks
 * `TableHead`'s `h-10`.
 */
export const CaseTableSkeleton = ({ rows = 14 }: CaseTableSkeletonProps) => (
	<div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
		<Skeleton className="h-10 w-full shrink-0 rounded-none" />

		{Array.from({ length: rows }, (_, index) => (
			<Skeleton
				key={index}
				className="mt-px h-12 w-full shrink-0 rounded-none"
			/>
		))}
	</div>
)

/**
 * The page-level composite: the route's `pendingComponent` *and* the list
 * section's `Suspense` fallback.
 *
 * One definition serving both call sites — they fire at different moments
 * (route transition vs. lazy chunk + query) but must look identical, and two
 * hand-written copies drift. The wrapper repeats `CaseListShell`'s own wrapper
 * classes, gaps included.
 */
export const CaseManagementSkeleton = () => (
	<div className="flex h-full w-full flex-col items-start justify-start gap-6">
		<CaseToolbarSkeleton
			analyticsPrefix="air_case_management"
			searchPlaceholder="Search for name"
		/>

		<CaseSummarySkeleton analyticsPrefix="air_case_management" />

		<CaseTableSkeleton />
	</div>
)
